import { useEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { trackFailed } from '../../../../../../shared-web/track';
import { apiErrorCode, apiFailureReason, emberApi, isAuthRequired, wordsRefused, type FriendsPage, type TopicRoomRow, type TopicSeat } from '../../api';
import { Link } from '../../components/Link';
import { PageFailure } from '../../components/PageFailure';
import { PageLoading } from '../../components/PageLoading';
import { PresenceAvatar } from '../../components/PresenceAvatar';
import { goFriends, navigate } from '../../router';
import { useRequireAuth } from '../../use-require-auth';
import { fitHeight } from './friend-parts';
import { useFriendsConfig } from '../../client-config';
import { CHEVRON, PENCIL, RoomsFull, SeatToCome, TopicBar, countLine } from './topic-shared';

const PATH = '/friends/rooms/new';
const TILTS = ['-1.5deg', '1deg', '-0.5deg', '1.5deg'];
const TINTS = ['#e3ece0', '#fbe1cf', '#ece6f6', '#fff1c7'];

/** "a dog room", "an art room": the article the topic takes. */

/**
 * Put a topic up (/friends/rooms/new; 2026-10-26 founder, rooms by topic; mock RoomTopic → RoomSeat). Step one: what
 * they would like to talk about — typed, or one of the topics Ember heard in their conversation; the rooms others have
 * up sit beside it, to ask into instead (someone else's room opens on its own page). Step two: the seat they will show
 * up with — the line Ember drafts for this topic, theirs to change, under the room nickname and picture they will have
 * there (the seat is shown before it is taken: 2026-10-26 founder, a star and "You" said nothing).
 * "Add to my rooms" puts it up; when rooms near it are already going they are shown first, with "Start my own anyway".
 * A topic up waits for people on the New friends page, where this lands.
 */
/** The steps as they stood, on the history entry (see `kept` below). */
interface TopicNewKept {
  typed: string;
  step: 'seat' | 'near';
  drafted: { topic: string; line: string; seat: TopicSeat | null } | null;
  line: string;
  near: TopicRoomRow[];
}

export function TopicNewPage(_props: { search: URLSearchParams }): ReactElement {
  // Meet people's served limits and sentences (2026-11-01 Riley, client-config.ts): redrawn when the config changes
  const served = useFriendsConfig();
  const session = useRequireAuth(PATH);
  const [page, setPage] = useState<FriendsPage | null>(null);
  const [failed, setFailed] = useState<'' | 'not_eligible' | 'load_failed'>('');
  const [heard, setHeard] = useState<string[] | null>(null);   // the topics Ember heard in their conversation
  const [typed, setTyped] = useState('');
  const [step, setStep] = useState<'pick' | 'seat' | 'near' | 'full'>('pick');
  const [refused, setRefused] = useState('');                  // under the topic field: the topic did not pass review
  // The seat: the line Ember drafted for this topic and the seat it comes with (null while it drafts; no seat when that
  // failed), the line as it stands, open for editing.
  const [drafted, setDrafted] = useState<{ topic: string; line: string; seat: TopicSeat | null } | null>(null);
  const [line, setLine] = useState('');
  const [editing, setEditing] = useState(false);
  const [near, setNear] = useState<TopicRoomRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const asked = useRef('');

  const toAuth = (): void => navigate(`/auth?returnTo=${encodeURIComponent(PATH)}`, { replace: true });

  // Where they were, kept on this history entry (2026-10-27 Riley: a room opened from "Rooms like this are already up",
  // then Back, landed on step one): the steps live in this page's state, so coming back to the entry puts them back —
  // and only coming back to this one; arriving anew is a new entry with nothing on it.
  const kept = useRef(((typeof window !== 'undefined' ? window.history.state : null) as { topicNew?: TopicNewKept } | null)?.topicNew ?? null);
  useEffect(() => {
    const k = kept.current;
    if (!k) return;
    setTyped(k.typed); setDrafted(k.drafted); setLine(k.line); setNear(k.near); setStep(k.step);
  }, []);
  useEffect(() => {
    if (step !== 'seat' && step !== 'near') return;
    const snap: TopicNewKept = { typed, step, drafted, line, near };
    try { window.history.replaceState({ ...(window.history.state ?? {}), topicNew: snap }, ''); } catch { /* the steps start over */ }
  }, [typed, step, drafted, line, near]);

  useEffect(() => {
    if (session !== 'authenticated') return;
    emberApi.friends()
      .then((p) => {
        if (!p.eligible) { setFailed('not_eligible'); return; }
        setPage(p);
        // Holding as many as one may already: the way to make space, not a form that will be refused.
        if (p.rooms && p.rooms.cap !== null && p.rooms.held !== null && p.rooms.held >= p.rooms.cap && !kept.current) setStep('full');
      })
      .catch((err: unknown) => {
        if (isAuthRequired(err)) { toAuth(); return; }
        if (apiErrorCode(err) === 'not_eligible') { setFailed('not_eligible'); return; }
        trackFailed('topic_load', 'load_failed', { error_detail: apiFailureReason(err) });
        setFailed('load_failed');
      });
    // The topics Ember heard: a suggestion beside the field — the page stands without them.
    emberApi.friendDraft()
      .then((d) => setHeard(d.topics))
      .catch((err: unknown) => { trackFailed('topic_draft', apiFailureReason(err), { state: 'pick' }); setHeard([]); });
  }, [session]);

  if (failed === 'not_eligible') return <PageFailure title="This isn’t available right now." logState="not_eligible" />;
  if (failed) return <PageFailure title="Couldn’t open this page" logState="load_failed" retry />;
  if (!page) return <PageLoading />;

  const topic = typed.trim().replace(/\s+/g, ' ');
  const held = page.rooms?.held ?? null;
  const cap = page.rooms?.cap ?? null;
  const waiting = page.browse?.waiting ?? [];
  const open = page.browse?.open ?? [];
  const edited = drafted !== null && line.trim() !== drafted.line.trim();

  /** To the seat: the line Ember drafts for this topic (kept when they come back to the same topic). */
  const toSeat = (): void => {
    if (!topic) return;
    setRefused(''); setNotice(''); setStep('seat');
    // The same topic keeps its seat; one whose draft failed is drafted again (2026-10-27 walk-through: back and Next left an
    // empty seat with nothing to press until a reload).
    if (drafted?.topic === topic && drafted.seat) return;
    asked.current = topic;
    setDrafted(null); setLine(''); setEditing(false);
    emberApi.topicSeatDraft(topic)
      .then((d) => { if (asked.current !== topic) return; setDrafted({ topic, line: d.line, seat: d.seat }); setLine(d.line); setEditing(!d.line); })
      .catch((err: unknown) => {
        if (asked.current !== topic) return;
        trackFailed('topic_draft', apiFailureReason(err), { state: 'seat' });
        setDrafted({ topic, line: '', seat: null }); setEditing(true);
        setNotice('Couldn’t get your seat ready. Go back and try again.');
      });
  };

  const putUp = async (anyway: boolean): Promise<void> => {
    if (busy || !drafted?.seat) return;
    setBusy(true); setNotice('');
    try {
      const out = await emberApi.putUpTopic({ room_id: drafted.seat.room_id, topic, intro: line.trim(), intro_edited: edited, ...(anyway ? { anyway: true } : {}) });
      // Up: back to the /friends they came from, not a second one over it (release check 2026-10-27).
      if ('room_id' in out) { goFriends(); return; }
      setNear(out.near);
      setStep('near');
    } catch (err) {
      if (isAuthRequired(err)) { toAuth(); return; }
      const code = apiErrorCode(err);
      trackFailed('topic_submit', apiFailureReason(err));
      const words = wordsRefused(err, { topic: 'A topic', intro: 'Your line' });
      if (code === 'rooms_full') setStep('full');
      else if (code === 'not_eligible') setFailed('not_eligible');
      // The topic did not pass review: back to it, kept as typed. The line: here, open to change.
      else if (words?.field === 'topic') { setStep('pick'); setRefused(words.message); }
      else if (words) { setEditing(true); setNotice(words.message); }
      else if (code === 'topic_required') { setStep('pick'); setRefused('That one can’t be a room topic. Try another?'); }
      else setNotice('Couldn’t add that. Try again.');
    } finally { setBusy(false); }
  };

  // Back steps back through the page before it leaves it.
  const stepBack = (): boolean => {
    if (step === 'seat') { setStep('pick'); return true; }
    if (step === 'near') { setStep('seat'); return true; }
    return false;
  };
  const steps = (n: 1 | 2): ReactElement => (
    <span className="topic-steps" role="img" aria-label={`Step ${n} of 2`}>
      <i className="is-on" /><i className={n === 2 ? 'is-on' : undefined} />
    </span>
  );

  if (step === 'full') {
    return (
      <main className="topic-page">
        <TopicBar to="/friends" />
        <RoomsFull topic={topic || undefined} />
      </main>
    );
  }

  if (step === 'near') {
    return (
      <main className="topic-page" data-log-seen="topic_near" data-log-state={String(near.length)}>
        <TopicBar to="/friends" onIntercept={stepBack}>{steps(2)}</TopicBar>
        <div className="topic-body">
          <div className="topic-head">
            <span className="topic-kicker">good news!</span>
            <h1 className="topic-h1">Rooms like this are already up</h1>
            <p className="topic-lede">Ask to join one, or start your own.</p>
          </div>
          <ul className="topic-near">
            {near.map((r) => (
              <li key={r.room_id}>
                <Link to={`/friends/rooms/${encodeURIComponent(r.room_id)}`} className="topic-near-row" data-log="topic_near_open" data-log-state={r.status ?? ''} data-log-match-id={r.room_id}>
                  <span className="topic-near-text">
                    <span className="topic-near-name" dir="auto">{r.name}</span>
                    <span className="topic-near-sub">{countLine(r)}</span>
                  </span>
                  {CHEVRON}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="topic-foot">
          {notice && <p className="topic-notice" role="alert">{notice}</p>}
          <button type="button" className="topic-btn is-ghost" disabled={busy} data-log="topic_anyway" onClick={() => { void putUp(true); }}>
            Start my own anyway
          </button>
        </div>
      </main>
    );
  }

  if (step === 'seat') {
    return (
      <main className="topic-page" data-log-seen="topic_seat">
        <TopicBar to="/friends" onIntercept={stepBack}>{steps(2)}</TopicBar>
        <div className="topic-body">
          <div className="topic-head">
            <span className="topic-kicker">last one!</span>
            {/* As the friend card's last step says it (2026-10-27 walk-through: "Here's you in a Lost my dad room" — a topic is often a phrase). */}
            <h1 className="topic-h1">Here’s how you’ll show up</h1>
          </div>
          <section className="topic-seat-card" aria-label="Your seat">
            {drafted?.seat ? <PresenceAvatar avatarKey={drafted.seat.avatar} size={88} /> : <SeatToCome size={88} />}
            {drafted?.seat && <span className="topic-seat-name" dir="auto">{drafted.seat.name}</span>}
            {drafted === null ? (
              <p className="topic-seat-line is-drafting" role="status">Drafting your line…</p>
            ) : editing ? (
              <div className="topic-seat-edit">
                <textarea className="topic-seat-field" value={line} maxLength={served.limits.room_intro_max_chars} rows={3} ref={fitHeight} onInput={(e) => fitHeight(e.currentTarget)} autoFocus
                  placeholder={`A line about you and “${topic}”`} aria-label="The line on your seat" onChange={(e) => setLine(e.target.value)} />
                {line.trim() && <button type="button" className="topic-link" onClick={() => setEditing(false)}>Done</button>}
              </div>
            ) : (
              <>
                <p className="topic-seat-line" dir="auto">{line}</p>
                <button type="button" className="topic-edit" data-log="topic_line_edit" onClick={() => setEditing(true)}>{PENCIL} Edit my line</button>
              </>
            )}
          </section>
          <span className="topic-note is-center">your nickname in rooms, never your real name</span>
          <ul className="topic-facts">
            <li>3 to 8 of you, plus me</li>
            <li>Drop in whenever</li>
            <li>Leave anytime</li>
          </ul>
        </div>
        <div className="topic-foot">
          <p className="topic-small">{served.words.topic_card_seen}</p>
          {notice && <p className="topic-notice" role="alert">{notice}</p>}
          <button type="button" className="topic-btn" disabled={busy || !drafted?.seat} data-log="topic_submit" data-log-state={edited ? 'edited' : 'drafted'}
            onClick={() => { void putUp(false); }}>
            Add to my rooms
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="topic-page" data-log-seen="topic_pick">
      <TopicBar to="/friends">{steps(1)}</TopicBar>
      <div className="topic-body">
        <div className="topic-head">
          <span className="topic-kicker">a room = a few of you + me</span>
          <h1 className="topic-h1">What would you like to talk about?</h1>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); toSeat(); }}>
          <input className={`topic-field${topic ? ' is-on' : ''}`} value={typed} maxLength={served.limits.room_topic_max_chars} placeholder="Anything on your mind"
            aria-label="Room topic" aria-invalid={Boolean(refused)} aria-describedby={refused ? 'topic-refused' : undefined}
            onChange={(e) => { setTyped(e.target.value); setRefused(''); }} />
          {refused && <p id="topic-refused" className="topic-refused" role="alert">{refused}</p>}
        </form>

        {(heard === null || heard.length > 0) && (
          <div className="topic-group">
            <span className="topic-group-head">From our chats</span>
            <div className="topic-chips">
              {heard === null
                ? [0, 1, 2].map((k) => <span key={k} className="topic-chip is-ghost" style={{ rotate: TILTS[k] }} aria-hidden />)
                : heard.map((t, k) => (
                  <button key={t} type="button" className={`topic-chip${topic === t ? ' is-on' : ''}`} aria-pressed={topic === t}
                    style={{ rotate: TILTS[k % 4], '--topic-tint': TINTS[k % 4] } as CSSProperties}
                    data-log="topic_pick" data-log-state="heard" onClick={() => { setTyped(t); setRefused(''); }}>
                    <span dir="auto">{t}</span>
                  </button>
                ))}
            </div>
          </div>
        )}

        {waiting.length > 0 && (
          <div className="topic-group">
            <span className="topic-group-head">Others are waiting on</span>
            <div className="topic-chips">
              {waiting.map((r, k) => (
                <Link key={r.room_id} to={`/friends/rooms/${encodeURIComponent(r.room_id)}`} className="topic-chip" style={{ rotate: TILTS[(k + 1) % 4] }}
                  data-log="topic_browse_open" data-log-state="waiting" data-log-match-id={r.room_id}>
                  <span dir="auto">{r.name}</span><span className="topic-chip-n">· {r.present} waiting</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {open.length > 0 && (
          <div className="topic-group">
            <span className="topic-group-head">Open now, ask to join</span>
            <div className="topic-chips">
              {open.map((r, k) => (
                <Link key={r.room_id} to={`/friends/rooms/${encodeURIComponent(r.room_id)}`} className="topic-chip is-open" style={{ rotate: k % 2 ? '1deg' : '-1deg' }}
                  data-log="topic_browse_open" data-log-state="open" data-log-match-id={r.room_id}>
                  <span dir="auto">{r.name}</span><span className="topic-chip-n">· {r.present} inside</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <span className="topic-note">I’ll bring in people who picked something close, too.</span>
      </div>
      <div className="topic-foot">
        {cap !== null && held !== null && (
          <div className="topic-held">
            <span className="topic-held-dots" aria-hidden>{Array.from({ length: cap }, (_, i) => <i key={i} className={i < held ? 'is-on' : undefined} />)}</span>
            Up to {cap} rooms at a time, looking or chatting
          </div>
        )}
        <button type="button" className="topic-btn" disabled={!topic} data-log="topic_next" onClick={toSeat}>Next</button>
      </div>
    </main>
  );
}
